import { __ } from '@wordpress/i18n';
import type {
	BoxControlInputControlProps,
	BoxControlProps,
	BoxControlValue,
	CustomValueUnits,
	Preset,
} from './types';

export const CUSTOM_VALUE_SETTINGS: CustomValueUnits = {
	px: { max: 300, step: 1 },
	'%': { max: 100, step: 1 },
	vw: { max: 100, step: 1 },
	vh: { max: 100, step: 1 },
	em: { max: 10, step: 0.1 },
	rm: { max: 10, step: 0.1 },
	svw: { max: 100, step: 1 },
	lvw: { max: 100, step: 1 },
	dvw: { max: 100, step: 1 },
	svh: { max: 100, step: 1 },
	lvh: { max: 100, step: 1 },
	dvh: { max: 100, step: 1 },
	vi: { max: 100, step: 1 },
	svi: { max: 100, step: 1 },
	lvi: { max: 100, step: 1 },
	dvi: { max: 100, step: 1 },
	vb: { max: 100, step: 1 },
	svb: { max: 100, step: 1 },
	lvb: { max: 100, step: 1 },
	dvb: { max: 100, step: 1 },
	vmin: { max: 100, step: 1 },
	svmin: { max: 100, step: 1 },
	lvmin: { max: 100, step: 1 },
	dvmin: { max: 100, step: 1 },
	vmax: { max: 100, step: 1 },
	svmax: { max: 100, step: 1 },
	lvmax: { max: 100, step: 1 },
	dvmax: { max: 100, step: 1 },
};

export const LABELS = {
	all: __( 'All sides' ),
	top: __( 'Top side' ),
	bottom: __( 'Bottom side' ),
	left: __( 'Left side' ),
	right: __( 'Right side' ),
	vertical: __( 'Top and bottom sides' ),
	horizontal: __( 'Left and right sides' ),
};

export const DEFAULT_VALUES = {
	top: undefined,
	right: undefined,
	bottom: undefined,
	left: undefined,
};

export const ALL_SIDES = [ 'top', 'right', 'bottom', 'left' ] as const;

/**
 * Gets the merged input value and unit from values data.
 *
 * @param values         Box values.
 * @param availableSides Available box sides to evaluate.
 *
 * @return A value + unit for the 'all' input.
 */
export function getMergedValue(
	values: BoxControlValue = {},
	availableSides: BoxControlProps[ 'sides' ] = ALL_SIDES
) {
	const sides = normalizeSides( availableSides );
	if (
		sides.every(
			( side: keyof BoxControlValue ) =>
				values[ side ] === values[ sides[ 0 ] ]
		)
	) {
		return values[ sides[ 0 ] ];
	}

	return undefined;
}

/**
 * Checks if the values are mixed.
 *
 * @param values         Box values.
 * @param availableSides Available box sides to evaluate.
 * @return Whether the values are mixed.
 */
export function isValueMixed(
	values: BoxControlValue = {},
	availableSides: BoxControlProps[ 'sides' ] = ALL_SIDES
) {
	const sides = normalizeSides( availableSides );
	return sides.some(
		( side: keyof BoxControlValue ) =>
			values[ side ] !== values[ sides[ 0 ] ]
	);
}

/**
 * Checks to determine if values are defined.
 *
 * @param values Box values.
 *
 * @return  Whether values are mixed.
 */
export function isValuesDefined( values?: BoxControlValue ) {
	return (
		values &&
		Object.values( values ).filter(
			// Switching units when input is empty causes values only
			// containing units. This gives false positive on mixed values
			// unless filtered.
			( value ) => !! value && /\d/.test( value )
		).length > 0
	);
}

/**
 * Normalizes provided sides configuration to an array containing only top,
 * right, bottom and left. This essentially just maps `horizontal` or `vertical`
 * to their appropriate sides to facilitate correctly determining value for
 * all input control.
 *
 * @param sides Available sides for box control.
 * @return Normalized sides configuration.
 */
export function normalizeSides( sides: BoxControlProps[ 'sides' ] ) {
	const filteredSides: ( keyof BoxControlValue )[] = [];

	if ( ! sides?.length ) {
		return ALL_SIDES;
	}

	if ( sides.includes( 'vertical' ) ) {
		filteredSides.push( ...( [ 'top', 'bottom' ] as const ) );
	} else if ( sides.includes( 'horizontal' ) ) {
		filteredSides.push( ...( [ 'left', 'right' ] as const ) );
	} else {
		const newSides = ALL_SIDES.filter( ( side ) => sides.includes( side ) );
		filteredSides.push( ...newSides );
	}

	return filteredSides;
}

/**
 * Return the allowed sides based on the sides configuration.
 *
 * @param sides Sides configuration.
 * @return Allowed sides.
 */
export function getAllowedSides(
	sides: BoxControlInputControlProps[ 'sides' ]
) {
	const allowedSides: Set< keyof BoxControlValue > = new Set(
		! sides ? ALL_SIDES : []
	);
	sides?.forEach( ( allowedSide ) => {
		if ( allowedSide === 'vertical' ) {
			allowedSides.add( 'top' );
			allowedSides.add( 'bottom' );
		} else if ( allowedSide === 'horizontal' ) {
			allowedSides.add( 'right' );
			allowedSides.add( 'left' );
		} else {
			allowedSides.add( allowedSide );
		}
	} );
	return allowedSides;
}

/**
 * Checks if a value is a preset value.
 *
 * @param value     The value to check.
 * @param presetKey The preset key to check against.
 * @return Whether the value is a preset value.
 */
export function isValuePreset( value: string, presetKey: string ) {
	return value.startsWith( `var:preset|${ presetKey }|` );
}

/**
 * Returns the index of the preset value in the presets array.
 *
 * @param value     The value to check.
 * @param presetKey The preset key to check against.
 * @param presets   The array of presets to search.
 * @return The index of the preset value in the presets array.
 */
export function getPresetIndexFromValue(
	value: string,
	presetKey: string,
	presets: Preset[]
) {
	if ( ! isValuePreset( value, presetKey ) ) {
		return undefined;
	}

	const match = value.match(
		new RegExp( `^var:preset\\|${ presetKey }\\|(.+)$` )
	);
	if ( ! match ) {
		return undefined;
	}
	const slug = match[ 1 ];
	const index = presets.findIndex( ( preset ) => {
		return preset.slug === slug;
	} );

	return index !== -1 ? index : undefined;
}

/**
 * Returns the preset value from the index.
 *
 * @param index     The index of the preset value in the presets array.
 * @param presetKey The preset key to check against.
 * @param presets   The array of presets to search.
 * @return The preset value from the index.
 */
export function getPresetValueFromIndex(
	index: number,
	presetKey: string,
	presets: Preset[]
) {
	const preset = presets[ index ];
	return `var:preset|${ presetKey }|${ preset.slug }`;
}
